import _ from 'lodash'
import moment from 'moment'
import Bluebird from 'bluebird'
import { EventEmitter } from 'fbemitter'
import { Collection } from 'shared-libs/models/collection'
import { VisibilityTimer } from 'browser/app/utils/visibility-timer'

type Precision = 'year' | 'month' | 'week' | 'day'

const DEFAULT_REFRESH_INTERVAL_MS = 60000

/**
 * Manages multiple Collections that each cover the loading of all data within a datetime interval.
 *
 * Assumes the interval cannot change during its lifecycle.
 */
export class IntervalCollectionManager extends EventEmitter {
  public static readonly ON_LOAD_END = 'onloadend'

  /**
   * Maps a month-year string key, like "2026-01", to a Collection that is responsible for loading
   * all data for a given month.
   */
  private collectionByInterval: Map<string, Collection> = new Map()

  /**
   * A subset of `collectionByInterval`'s keys that is eligible for auto-refresh.
   */
  private activeIntervals: Set<string> = new Set()

  private timer: VisibilityTimer
  private loadPromise: Bluebird<any> | null = null

  /**
   * Assumes the entityType has both a start and end datetime, not just a single datetime field.
   */
  constructor(
    private api: any,
    private entityType: string,
    private startPath: string,
    private endPath: string,
    private baseFilters: any[] = [],
    private baseOrders: any[] = [],
    private refreshInterval = DEFAULT_REFRESH_INTERVAL_MS,
    private precision: Precision = 'month'
  ) {
    super()

    this.timer = new VisibilityTimer(this.handleRefresh, this.refreshInterval)
  }

  public destroy(): void {
    this.loadPromise?.cancel()
    this.loadPromise = null
    this.timer.dispose()
    this.removeAllListeners()
  }

  /**
   * Loads data for the given date range, building backing Collection(s) as necessary.
   *
   * The only times when requests should be made are:
   *  - when the collection is newly constructed.
   *  - when the auto-refresh timer fires AND the collection's key is in the active set.
   *
   * @returns A promise that completes when all loading is finished, but returns no data.
   */
  public load(start: string, end: string, force = false): Bluebird<any> {
    console.log(`load range ${start} - ${end}`)
    if (this.loadPromise) {
      this.loadPromise.cancel()
      this.loadPromise = null
    }

    this.activeIntervals.clear()
    if (force) {
      this.collectionByInterval.clear()
    }

    const keys = this.splitInterval(start, end)
    console.log(`load split interval: ${keys}`)

    const createdKeys: string[] = []

    for (const key of keys) {
      this.activeIntervals.add(key)
      if (!this.collectionByInterval.has(key)) {
        const collection = this.createCollection(key)
        this.collectionByInterval.set(key, collection)
        createdKeys.push(key)
      }
    }

    if (createdKeys.length > 0) {
      const loadPromises = createdKeys.map((key) => this.reloadKey(key))
      this.loadPromise = Bluebird.all(loadPromises).finally(() => {
        this.loadPromise = null
      })
      return this.loadPromise
    }

    return Bluebird.resolve()
  }

  private createCollection(key: string): Collection {
    const monthStart = moment(key, this.keyFormat).startOf(this.precision).toDate()
    const monthEnd = moment(key, this.keyFormat).endOf(this.precision).toDate()

    const filters = [
      ...this.baseFilters,
      { path: this.startPath, type: 'range', lte: monthEnd },
      { path: this.endPath, type: 'range', gte: monthStart },
    ]

    const query = this.api
      .getStore()
      .query()
      .setEntityType(this.entityType)
      .setFilters(filters)
      .setOrders(this.baseOrders)
      .setDebug({ context: 'model--IntervalCollectionManager' })

    return new Collection(this.api, query)
  }

  public setFilters(filters: any[]): void {
    this.baseFilters = filters
    this.collectionByInterval.clear()
  }

  private handleRefresh = (): Bluebird<any> => {
    if (this.loadPromise) {
      return Bluebird.resolve()
    }

    const activeKeys = [...this.activeIntervals.keys()]
    const refreshPromises = _.map(activeKeys, (key) => this.reloadKey(key))

    this.loadPromise = Bluebird.all(refreshPromises).finally(() => {
      this.loadPromise = null
    })

    return this.loadPromise
  }

  private reloadKey(key: string): Bluebird<void> {
    console.error(`Reloading ${key}...`)
    return new Bluebird(async (res) => {
      const collection = this.collectionByInterval.get(key)!
      await collection.find()
      // console.log(`[${key}] find() loaded ${collection.entities.length} entities`)
      while (collection.hasNext()) {
        await collection.next()
        // console.log(`[${key}] next() loaded ${collection.entities.length} entities total`)
      }
      this.emit(IntervalCollectionManager.ON_LOAD_END, {
        key,
        entities: collection.entities,
      })
      res()
    })
  }

  private splitInterval(start: string, end: string): string[] {
    const startMoment = moment(start).startOf(this.precision)
    const endMoment = moment(end).startOf(this.precision)
    const diff = endMoment.diff(startMoment, this.precision)

    const result: string[] = []

    for (let i = 0; i <= diff; i++) {
      result.push(startMoment.clone().add(i, this.precision).format(this.keyFormat))
    }

    return result
  }

  private get keyFormat(): string {
    switch (this.precision) {
      case 'year':
        return 'YYYY'
      case 'month':
        return 'YYYY-MM'
      case 'week':
        return 'GGGG-[W]WW'
      case 'day':
        return 'YYYY-MM-DD'
      default:
        throw new Error(`unsupported precision value ${this.precision}`)
    }
  }
}
